import { createAsyncThunk } from '@reduxjs/toolkit'
import { BookingPeriod } from '@spiaggeit/spit-datepicker'

import {
  fetchPendingCart,
  PendingCartResponse,
} from '@/api/fetchPendingCart.ts'
import { Period } from '@/models/insertPeriod'
import { appSlice } from '@/store/appSlice.ts'
import { RootState } from '@/store/configureStore.ts'
import { insertPeriodSlice } from '@/store/insertPeriodSlice.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { getPeriodFromReservationModels } from '@/utils/mapReservationModelsToQuoteReservations'
import {
  bookingPeriodFromPendingCartPeriod,
  clearPendingCartContext,
  getPendingCartPeriod,
  getPendingCartToken,
  isPendingCartPeriodMatch,
  pendingCartPeriodFromUnixTimestamps,
} from '@/utils/pendingCartToken'

export type RestorePendingCartResult = {
  cartDetail: PendingCartResponse
  periodWasRestored: boolean
  shouldRestoreCart: boolean
}

export const restorePendingCart = createAsyncThunk<
  RestorePendingCartResult,
  void,
  { rejectValue: string; state: RootState }
>('cart/restorePendingCart', async (_, thunkAPI) => {
  const rootState = thunkAPI.getState()
  const license = licenseSlice.selectors.licenseCode(rootState)
  const country = appSlice.selectors.country(rootState)
  const pendingCartToken = getPendingCartToken(license)

  if (!pendingCartToken) {
    throw thunkAPI.rejectWithValue('no-pending-cart-token')
  }

  const response = await fetchPendingCart({
    country,
    licenseName: license,
    pendingCartToken,
  })

  if (response.status === 'error') {
    clearPendingCartContext(license)
    throw thunkAPI.rejectWithValue(
      typeof response.error === 'string'
        ? response.error
        : 'pending-cart-not-found'
    )
  }

  const cartDetail = response.data.result
  const periodData = getPeriodFromReservationModels(
    cartDetail.cart.reservationModels
  )

  const pendingPeriodFromApi =
    periodData !== null
      ? pendingCartPeriodFromUnixTimestamps(
          periodData.startDate,
          periodData.endDate,
          periodData.halfDay,
          periodData.seasonal
        )
      : null

  const pendingPeriod = getPendingCartPeriod(license) ?? pendingPeriodFromApi

  const currentPeriod: Period | null =
    insertPeriodSlice.selectors.period(rootState)
  const currentBookingPeriod: BookingPeriod | null =
    insertPeriodSlice.selectors.bookingPeriod(rootState)

  const shouldRestorePeriod: boolean =
    currentPeriod === null && pendingPeriodFromApi !== null

  if (shouldRestorePeriod && pendingPeriodFromApi !== null) {
    thunkAPI.dispatch(
      insertPeriodSlice.actions.setPeriod({
        end: pendingPeriodFromApi.end,
        start: pendingPeriodFromApi.start,
      })
    )
    thunkAPI.dispatch(
      insertPeriodSlice.actions.setBookingPeriod(
        bookingPeriodFromPendingCartPeriod(pendingPeriodFromApi)
      )
    )
  }

  const periodForMatch = currentPeriod ?? pendingPeriodFromApi
  const bookingPeriodForMatch =
    shouldRestorePeriod && pendingPeriodFromApi !== null
      ? bookingPeriodFromPendingCartPeriod(pendingPeriodFromApi)
      : currentBookingPeriod

  const shouldRestoreCart =
    pendingPeriod !== null &&
    periodForMatch !== null &&
    isPendingCartPeriodMatch(
      periodForMatch,
      bookingPeriodForMatch,
      pendingPeriod
    )

  return {
    cartDetail,
    periodWasRestored: shouldRestorePeriod,
    shouldRestoreCart,
  }
})
